import React, { useState, useEffect, useRef } from "react";
import { Button, Form, DropdownButton, Dropdown, Col, Row } from "react-bootstrap";
import Select from "react-select";
import { Modal, ModalHeader, ModalBody, Table } from "reactstrap";
import DateRangePicker from "@wojtekmaj/react-daterange-picker";
import "@wojtekmaj/react-daterange-picker/dist/DateRangePicker.css";
import { useForm, Controller } from "react-hook-form";
import { Loading } from "./Loading";
/*import * as XLSX from "xlsx/xlsx.mjs";*/
import * as XLSX from "@e965/xlsx/xlsx.mjs";
import ServiceCalls from "../utils/serviceCalls.js";
import CommonUtils from "../utils/siteUtils";
import "../styles/Admin.css";
import "../styles/PopUps.css";
import '../styles/Calendar.css';
import DataTable from "react-data-table-component";
import { VendorSetIDDropdown } from '../components/VendorSetIDDropdown.js';


/**
 * Admin audit logs search components
 * AdminAuditLogs - main component
 * AdminAuditTypeTable - table component
 * AdminAuditViewDetails - modal component
 * @returns
 */
export const AdminAuditLogs = (props) => {
	const [auditLogs, setAuditLogs] = useState(null);
	const [contactSearchString, setContactSearchString] = useState("");
	const [messageSearchString, setMessageSearchString] = useState("");
	const [invDateError, setInvDateError] = useState("");
	const [invDateRng, setInvDateRng] = useState([null, null]);
	const [auditType, setAuditType] = useState([]);
	const [auditDescriptions, setAuditDescriptions] = useState(null);
	const [isLoading, setIsLoading] = useState(false);
	const [detailsOpen, setDetailsOpen] = useState(false);
	const [selectedLog, setSelectedLog] = useState(null);
	const [isValid, setIsValid] = useState(true);
	const [totalRows, setTotalRows] = useState(0);
	const isMounted = useRef(true);
	const [selectedVendorSetID, setSelectedVendorSetID] = useState("GLOBAL");

	const {
		control,
		/*formState: { errors },*/
	} = useForm();

	//Get last 25 audits on mount
	useEffect(() => {
		const fetchData = async () => {
			await handleSearch(true);
		};

		fetchData();

		return () => {
			isMounted.current = false;
		};
	}, []);

	// Opens the details modal and sets the selected log
	const openDetails = (log) => {
		setSelectedLog(log);
		setDetailsOpen(true);
	};
	// Closes the details modal and clears the selected log
	const closeDetails = () => {
		setSelectedLog(null);
		setDetailsOpen(false);
	};

	const onClearForm = () => {
		setSelectedVendorSetID("GLOBAL");
		handleSearch(true);
		setContactSearchString("");
		setMessageSearchString("");
		setInvDateRng([null, null]);
		setAuditType([]);
		setIsValid(true);
	};

	const formValidate = (input) => {
		var regex = /^[^<>"()]*$/;  //DE1270974 - Allow characters that are allowed in email field
		return regex.test(input);
	};

	const handleSearch = async (recentSearch) => {
		// If not a recent search and all search criteria are null/empty
		setIsValid(true);
		//Check if page access is still allowed
		props.onChildUpdate();
		if ((!recentSearch && !selectedVendorSetID && !contactSearchString && !messageSearchString && !invDateRng[0] && !invDateRng[1] && auditType.length === 0) || !formValidate(contactSearchString) || !formValidate(messageSearchString)) {
			setIsValid(false);
			return;
		}

		setIsLoading(true);

		//Form searchInfo object to pass to service call
		//If recentSearch flag is true, then get last 25 audits in DB.
		//Otherwise, search based on search criterias
		var searchInfo = recentSearch
			? null
			: {
				vendorSetId: selectedVendorSetID || null,
				contactSearchString: contactSearchString,
				messageSearchString: messageSearchString,
				DateRange: {
					startDate: invDateRng[0] ? new Date(invDateRng[0].toDateString()) : null,
					// 	DE841465: Inconsistent Results w/Date Search
					endDate: invDateRng[1] ? new Date(invDateRng[1].getFullYear(), invDateRng[1].getMonth(), invDateRng[1].getDate(), 23, 59, 59, 999) : null,
				},
				auditTypes: auditType,

			};

		//Server call to get audit logs
		ServiceCalls.getAdminAuditResults(searchInfo)
			.then(function (data) {
				if (data && isMounted.current) {
					//DE991469 - Filter out AuditOwner records if selectedVendorSetID is not GLOBAL or ADMIN
					if (!recentSearch && selectedVendorSetID !== "GLOBAL" && selectedVendorSetID !== "ADMIN") {
						data = data.filter((log) => log.auditContact.contactId !== 999999999);
					}

					setAuditLogs(data); // Store the response data in the auditLogs state variable  
					setTotalRows(data.length);
					const auditDescriptionList = data[0]?.auditDescriptions;
					if (auditDescriptionList) {
						setAuditDescriptions(auditDescriptionList);
					}
				}
			})
			.catch((error) => {
				console.error("Error occurred while fetching admin audit results:", error);
			})
			.finally(() => {
				if (isMounted.current) {
					setIsLoading(false);
					setIsValid(true);
				}
			});
	};

	const hasValidDates = (values) => {
		if (values[0] !== null && values[1] !== null) {
			return values[0] < values[1];
		}
		return true;
	};
	const getMinDate = () => {
		var curDate = new Date();
		var minYear = curDate.getFullYear() - 2;
		var result = new Date(minYear, 0, 1);
		return result;
	};
	const prepForExport = () => {
		return auditLogs.map((log) => ({
			"Audit ID": log.auditId,
			"Supplier Set ID": log.auditContact.contactId === 999999999 ? "ADMIN" : log.vendorSetId, //US9106875 Setid missing in exports for Audit Owner
			"Audit Type": log.auditType,
			"Contact Email": log.auditContact.email,
			"Contact Name": log.auditContact.name,
			// DE840652 Date Wrong Format in Exports
			"Create Date": log.auditDate ? CommonUtils.formatDate(log.auditDate, true) : "",
			"Message":log.message
		}));
	};

	const exportExcelResults = () => {
		const ws = XLSX.utils.json_to_sheet(prepForExport());
		const wb = XLSX.utils.book_new();
		XLSX.utils.book_append_sheet(wb, ws, "Audit Logs");
		XLSX.writeFile(wb, "AuditLogs.xlsx");
	};

	const exportCSVResults = () => {
		const ws = XLSX.utils.json_to_sheet(prepForExport());
		const csv = XLSX.utils.sheet_to_csv(ws);
		const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
		const url = URL.createObjectURL(blob);
		const link = document.createElement("a");
		link.href = url;
		link.download = "AuditLogs.csv";
		link.click();
	};

	const handleListLoaded = (isLoaded) => {

	};

	return (
		<div className="auditLogsContainer">
			<div id="auditLogsArea" className="flex-column borderCurve">
				<div className="auditLogsHeader">
					<h3>Audit Logs</h3>
					{isValid ? <></> : <span className="invalid">&nbsp;&nbsp;Invalid input</span>}
				</div>

				<Form
					onSubmit={(e) => {
						e.preventDefault();
						handleSearch(false);
					}}>

					<Row>
						<Col md={3}>
							<Form.Label className="formLabel">Supplier Set ID</Form.Label>
							<VendorSetIDDropdown
								currentAudience={selectedVendorSetID}
								onChange={setSelectedVendorSetID}
								onListLoaded={handleListLoaded}
								showGlobalOption={true}
								showAdminOption={true}
								showLabel={false}
								showSelectOption={false}
							/>
						</Col>

						<Col md={4} >
							<Form.Label className="formLabel">Contact</Form.Label>
							<Form.Control type="text" value={contactSearchString} placeholder="Enter contact email, last name or ID" onChange={(e) => setContactSearchString(e.target.value)} />
						</Col>
						<Col md={5} >
							<Form.Label className="formLabel">Audit Message </Form.Label>
							<Form.Control type="text" placeholder="Enter audit message" value={messageSearchString} onChange={(e) => setMessageSearchString(e.target.value)} />
						</Col>
					</Row>

					<Row>
						<Col md={4} >
							<Form.Label className="formLabel">Create Date Range</Form.Label>
							<Controller
								control={control}
								name="dateRange"
								render={({ field }) => (
									<DateRangePicker
										format="MM/dd/yyyy"
										className="form-control custom-date-range-picker"
										calendarClassName="searchCalendar"
										onChange={(value) => {
											if (value) {
												if (hasValidDates(value)) {
													setInvDateRng(value);
													setInvDateError("");
												} else {
													console.log("Invalid Dates");
													setInvDateError("Invalid Dates");
												}
											} else {
												setInvDateRng([null, null]);
												setInvDateError("");
											}
										}}
										value={invDateRng}
										showLeadingZeros={true}
										minDate={getMinDate()}
										minDetail={"year"}
										monthPlaceholde={"mm"}
										rangeDivider={" to "}
									/>
								)}
							/>
							<span className="error">{invDateError}</span>
						</Col>
						<Col md={5}>
							<label className="formLabel form-label" htmlFor="formAuditType">
								Audit Type
							</label>
							{/* US11013031 Multi-select dropdown for filtering audit logs by audit type */}
							<Select
								inputId="formAuditTypeSel"
								name="auditType"
								isMulti
								placeholder="Select Audit Type"
								classNamePrefix="auditTypeSelect"
								options={(auditDescriptions || []).map((type) => ({ value: type, label: type }))}
								value={auditType.map((type) => ({ value: type, label: type }))}
								onChange={(selectedOptions) => setAuditType(selectedOptions ? selectedOptions.map((opt) => opt.value) : [])}
								menuPortalTarget={document.body}
								styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
								/>
								</Col>
						<Col md={3} style={{ paddingLeft: "unset" }}>
							<div className="d-flex justify-content-end">
								<Button variant="secondary" className="mr-2 align-top auditLogsSearchButton" onClick={onClearForm} style={{ marginRight: "5px" }}>
									Clear
								</Button>
								<Button variant="primary" type="submit" className="mr-2 align-top auditLogsSearchButton">
									Search
								</Button>
							</div>
						</Col>
					</Row>
				</Form>
			</div>
			{!isLoading && (
				<div className="d-flex" style={{ float: "right" }}>
					<div className="searchResultsCnt">
						<span className="searchResultsPill">{totalRows}</span> Results
					</div>
					<DropdownButton title="Export Results" className="auditTypeExport align-top" variant="primary" style={{ marginTop: "24px", marginBottom: "2px", marginRight: "17px" }}>
						<Dropdown.Item eventKey="1" onClick={exportCSVResults}>
							CSV
						</Dropdown.Item>
						<Dropdown.Item eventKey="2" onClick={exportExcelResults}>
							Excel
						</Dropdown.Item>
					</DropdownButton>
				</div>
			)}
			{isLoading && <Loading />}
			{!isLoading && (
				<div id="auditLogsArea" className="flex-column borderCurve" style={{ marginTop: "15px" }}>
					<AdminAuditLogsTable data={auditLogs} openDetails={openDetails} />
					{selectedLog && <AdminAuditViewDetails data={selectedLog} open={detailsOpen} toggle={closeDetails} />}
				</div>
			)}
		</div>
	);
};

const AdminAuditLogsTable = ({ data, openDetails, auditLogs}) => {
	//DE840678: Create Date Secondary Sort Needed
	//Custom sort for if audit create date's are equal, then fallback to sort by audit ID



	const customDateSort = (rowA, rowB) => {
		const dateA = new Date(CommonUtils.formatDate(rowA.auditDate, true)).getTime();
		const dateB = new Date(CommonUtils.formatDate(rowB.auditDate, true)).getTime();

		// Compare the dates first
		if (dateA > dateB) return 1;
		if (dateA < dateB) return -1;

		// If the dates are equal, compare by audit ID
		return rowA.auditId > rowB.auditId ? 1 : -1;
	};

	//Table columns..
	const columns = [
		{
			name: "Audit ID",
			selector: (row) => row.auditId,
			sortable: true,
			maxWidth: "100px",
			minWidth:"100px"
		},
		{
			name: "Supplier Set ID",
			selector: (row) => {
				if (row.auditContact.contactId === 999999999) {
					return "ADMIN";
				} else {
					return row.vendorSetId;
				}
			},
			sortable: true,
			maxWidth: "140px",
			minWidth: "100px",
		},
		{
			name: "Audit Type",
			selector: (row) => row.auditType,
			sortable: true,
			sortFunction: (rowA, rowB) => CommonUtils.customStringSort(rowA, rowB, (row) => row.auditType),
			minWidth: "180px",
			maxWidth:"240px"
		},
		{
			name: "Contact Email",
			selector: (row) => row.auditContact.email,
			sortable: true,
			sortFunction: (rowA, rowB) => CommonUtils.customStringSort(rowA, rowB, (row) => row.auditContact.email),
			minWidth: "200px",
			maxWidth:""
		},
		{
			name: "Contact Name",
			selector: (row) => row.auditContact.name,
			sortable: true,
			maxWidth: "215px",
			minWidth: "210px",
			sortFunction: (rowA, rowB) => CommonUtils.customStringSort(rowA, rowB, (row) => row.auditContact.name),
		},
		{
			name: "Create Date",
			selector: (row) => new Date(CommonUtils.formatDate(row.auditDate, true)).getTime(),
			sortable: true,
			maxWidth: "160px",
			minWidth: "120px",
			format: (row) => CommonUtils.formatDate(row.auditDate, true),
			sortFunction: customDateSort,
		},
		{
			name: "",
			button: "true",
			cell: (row) => (
				<Button variant="primary" style={{ padding: "5px" }} onClick={() => openDetails(row)}>
					View Details
				</Button>
			),
		},
	];

	//Custom styles for the table
	const customStyles = {
		table: {
			style: {
				padding: "0",
				margin: "0",
			},
		},
		responsiveWrapper: {
			style: {
				borderRadius: "unset",
			},
		},
		headCells: {
			style: {
				fontSize: "14px",
				justifyContent: "left !important",
				color: "white",
				paddingLeft: "1%",
				borderRight: "1px solid white",
				fontWeight: "400",
				wrap: true,
				whiteSpace: "normal",
				wordWrap: "break-word",
			},
		},
		head: {
			style: {
				zIndex: "99",
			},
		},
		headRow: {
			style: {
				backgroundColor: "#003C71",
			},
		},
	};
	return data ? <DataTable columns={columns} data={data} pagination striped responsive customStyles={customStyles} paginationPerPage={25} /> : null;
};
const AdminAuditViewDetails = ({ data, open, toggle }) => {
	const { auditId, auditType, auditContact, message, auditDate } = data;

	//US10765600 Converts PascalCase or camelCase keys to readable labels
	const formatKey = (key) => {
		// Insert a space before sequences of uppercase letters followed by lowercase, or before a single uppercase preceded by lowercase
		const spaced = key
			.replace(/([a-z])([A-Z])/g, "$1 $2")
			.replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2");
		// Capitalize common abbreviations
		return spaced
			.replace(/\bId\b/g, "ID")
			.replace(/\bDt\b/g, "Date")
			.replace(/\bCnt\b/g, "Count");
	};

	// Parses the message into a text and json object for when the message contains a json object
	const parseMessage = (message) => {
		const jsonStartIndex = message.indexOf("{");
		if (jsonStartIndex !== -1) {
			const text = message.substring(0, jsonStartIndex);
			const jsonString = message.substring(jsonStartIndex);
			try {
				const jsonObj = JSON.parse(jsonString);
				const prettyJson = JSON.stringify(jsonObj, null, 2);
				return { text, prettyJson }; // If the json is valid, return the text/string and json object
			} catch (e) {
				return { text: message }; // If the json is invalid, just return the message
			}
		} else {
			return { text: message }; // If there is no json, just return the message
		}
	};

	// Gets the text and json object from the message
	const { text, prettyJson } = parseMessage(message);

	return (
		<Modal centered={true} fade={false} isOpen={open} toggle={toggle} size="xl">
			<ModalHeader toggle={toggle} className="view-request-header" close={<></>}>
				Audit Details
			</ModalHeader>
			<ModalBody>
				<Form>
					<Row>
						<Col sm={6}>
							<h4 className="modal-subheader">Audit</h4>
							<Row>
								<Col sm={5}>
									<strong>Audit ID</strong>
								</Col>
								<Col sm={7}>{auditId}</Col>
							</Row>
							<Row>
								<Col sm={5}>
									<strong className="textWrap">Audit Type</strong>
								</Col>
								<Col sm={7}>{auditType}</Col>
							</Row>
							<Row>
								<Col sm={5}>
									<strong>Create Date</strong>
								</Col>
								<Col sm={7}>{auditDate ? CommonUtils.formatDate(auditDate, true) : null}</Col>
							</Row>
						</Col>
						<Col sm={6}>
							<h4 className="modal-subheader">Contact</h4>
							<Row>
								<Col sm={5}>
									<strong>Contact ID</strong>
								</Col>
								<Col sm={7}>{auditContact.contactId}</Col>
							</Row>
							<Row>
								<Col sm={5}>
									<strong>Contact Email</strong>
								</Col>
								<Col sm={7} className="textWrap">
									{auditContact.email}
								</Col>
							</Row>
							<Row>
								<Col sm={5}>
									<strong>Contact Name</strong>
								</Col>
								<Col sm={7} className="textWrap">
									{auditContact.name}
								</Col>
							</Row>
						</Col>
					</Row>
					<hr />
					<Row>
						<Col sm={12} className="auditLogsViewDetailsTableColumn">
							<h4 className="modal-subheader" style={{ fontFamily: "unset" }}>
								Audit Message
							</h4>
							{prettyJson ? (
								<Table bordered className="auditLogsViewDetailsTable">
									<tbody>
										{text
											.split("[")
											.filter((item) => item)
											.map(
												(
													item,
													index // Splits the text into an array of strings and maps each string to a table row
												) => (
													<tr key={index}>
														<td className="auditLogsViewDetailsTableItem">{item.split("]")[0]}</td>
														<td className="auditLogsViewDetailsTableItem">{item.split("]")[1]}</td>
													</tr>
												)
											)}
										{Object.entries(JSON.parse(prettyJson)).map(([key, value], index) => {
											let formattedValue;
											// Check if value is a string that can be parsed as a date
											if (typeof value === "string") {
												const date = new Date(value);
												// Format if it looks like a date (includes "/" or is ISO 8601 format with "T")
												if (!isNaN(date.getTime()) && (value.includes("/") || /^\d{4}-\d{2}-\d{2}T/.test(value))) {
													formattedValue = CommonUtils.formatDate(date.toISOString(), true);
												} else {
													// It's a string but not a date, so display as is
													formattedValue = value;
												}
											} else if (Array.isArray(value)) {
												formattedValue = value.length === 0 ? "None" : JSON.stringify(value, null, 2);
											} else {
												// It's not a string, so stringify the value
												formattedValue = JSON.stringify(value);
											}
											return (
												<tr key={index}>
													<td className="auditLogsViewDetailsTableItem">{formatKey(key)}</td>
													<td className="auditLogsViewDetailsTableItem">
														{typeof value === "object" && !Array.isArray(value) && value !== null
															? <pre style={{ margin: 0 }}>{JSON.stringify(value, null, 2)}</pre>
															: formattedValue}
													</td>
												</tr>
											);
										})}
									</tbody>
								</Table>
							) : (
								<p>{text}</p>
							)}
						</Col>
					</Row>

					<Button className="request-details-popup-buttons" onClick={() => toggle()}>
						Close
					</Button>
				</Form>
			</ModalBody>
		</Modal>
	);
};
